import { useCallback, useState } from 'react'

import { Item } from '@inno-sdks/files'
import { toast } from '@inno-ui/toast'
import { useTranslation } from 'react-i18next'

import { useRenameItemMutation } from '../api/hooks/useRenameItem'
import { validateItemName } from '../utils'

export const useInlineRename = (item: Item) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'desktop.shortcut_item.context_menu'
  })
  const [isRenaming, setIsRenaming] = useState(false)
  const { mutateAsync, isPending } = useRenameItemMutation()

  const start = useCallback(() => {
    setIsRenaming(true)
  }, [])

  const onCancel = useCallback(() => {
    if (isPending) return
    setIsRenaming(false)
  }, [isPending])

  const onSubmit = useCallback(
    (newName: string) => {
      if (isPending) return
      setIsRenaming(false)

      if (!newName || newName === item.name) {
        return
      }

      const invalidName = validateItemName(newName)
      if (invalidName) {
        toast.dismiss()
        toast.error({ ...invalidName })
        return
      }

      const errorState: { title: string; subTitle?: string } = {
        title: t('failed_to_rename')
      }

      const promise = mutateAsync({ id: item.id, name: newName }).catch(
        error => {
          if (error instanceof Error && error.message) {
            errorState.subTitle = error.message
          }
          throw error
        }
      )

      toast.info({
        asyncHandler: promise,
        asyncStates: {
          loading: { title: t('renaming') },
          success: { title: t('renamed') },
          error: errorState
        }
      })
    },
    [isPending, item.id, item.name, mutateAsync, t]
  )

  return { isRenaming, start, onSubmit, onCancel }
}
